import { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import { MdPerson, MdWorkOutline, MdAccountBalance, MdBadge, MdStar, MdUploadFile } from "react-icons/md";
import Header from "../Header";
import LeftNavBar from "../LeftNavBar";
import PageHeader from "../PageHeader";
import { PageHeaderName } from "../../StyledCss/styledComponents";
import { PrimaryButton, SecondaryButton } from "../../StyledCss/styledbtn";
import EditEmployeeModal from "../EditEmployeeModal";
import {
  getAllActiveEmployees,
  getAllReleasedEmployees,
  getLeads,
} from "../../api/services/hroneApi";
import { convertKeysToCamelCase } from "../../util/convertCamelCase";
import formatDate from "../../util/formatdate";
import "./index.css";
import { usePermissions } from "../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../util/featureCodes";

const TABS = [
  { id: "overview", label: "Overview", path: "", permissionCode: FEATURE_CODES.EMPLOYEE_OVERVIEW_VIEW },
  { id: "aadhar", label: "Aadhar Card", path: "aadhar-card", permissionCode: FEATURE_CODES.EMPLOYEE_AADHAR_VIEW },
  { id: "pan", label: "PAN Card", path: "pan-card", permissionCode: FEATURE_CODES.EMPLOYEE_PAN_VIEW },
];

const TAB_BY_PATH = {
  "aadhar-card": "aadhar",
  "pan-card": "pan",
};

const AVATAR_COLORS = [
  "#e86020", "#4caf50", "#2196f3", "#9c27b0", "#ff5722", "#00897b", "#f06292",
];

const getInitials = (name = "") => {
  const initials = name.trim().split(/\s+/).map((w) => w[0]).join("");
  return (initials || "?").slice(0, 2).toUpperCase();
};

const getAvatarColor = (name = "") =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const EmployeeDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tab: tabPath } = useParams();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(FEATURE_CODES.EMPLOYEE_EDIT);
  const visibleTabs = TABS.filter((tab) => hasPermission(tab.permissionCode));

  const orgDetails = JSON.parse(localStorage.getItem("org"));
  const primaryColor = orgDetails?.primaryColor || "#e86020";

  const passedEmployee = location.state?.employee;
  const returnPath = location.state?.returnPath || "/allemployees";

  const [employee, setEmployee] = useState(passedEmployee);
  const [leads, setLeads] = useState(location.state?.leads || []);
  const [isEditing, setIsEditing] = useState(false);

  const requestedTab = TAB_BY_PATH[tabPath] || "overview";
  // If the URL points at a tab the user isn't permitted to see, fall back to
  // the first tab they do have access to (mirrors the Leave Management tab
  // gating pattern) instead of rendering a blank/inaccessible tab.
  const activeTab = visibleTabs.some((tab) => tab.id === requestedTab)
    ? requestedTab
    : visibleTabs[0]?.id;

  useEffect(() => {
    if (!location.state?.leads) fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await getLeads();
      if (response.status === 200) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (err) {
      // leads dropdown just stays empty if this fails — non-critical for viewing/editing
    }
  };

  const refreshEmployee = async () => {
    try {
      const [activeRes, releasedRes] = await Promise.all([
        getAllActiveEmployees(),
        getAllReleasedEmployees(),
      ]);
      const [activeData, releasedData] = await Promise.all([
        activeRes.json(),
        releasedRes.json(),
      ]);
      const merged = [...activeData, ...releasedData].map(convertKeysToCamelCase);
      const updated = merged.find((emp) => emp.id === employee?.id);
      if (updated) {
        setEmployee({
          ...updated,
          joiningDate: formatDate(updated.joiningDate),
          dateOfBirth: formatDate(updated.dateOfBirth),
        });
      }
    } catch (err) {
      // page keeps showing the last-known data if the refresh fails
    }
  };

  const handleBack = () => navigate(returnPath);
  const handleStartEdit = () => {
    if (!canEdit) return;
    setIsEditing(true);
  };
  const closeEditEmployeeModel = () => setIsEditing(false);
  const notifyUpdated = () => {
    toast.success("Employee Updated Successfully");
    refreshEmployee();
  };

  const goToTab = (tab) => {
    const suffix = tab.path ? `/${tab.path}` : "";
    navigate(`/employee/${employee.id}${suffix}`, {
      state: { employee, leads, returnPath },
    });
  };

  // Aadhar/PAN are just two more optional columns on the employee record, saved
  // through the same full "Edit Employee" form/endpoint as everything else — so
  // uploading from these tabs opens that form rather than hitting a separate call.
  const goToEditForUpload = () => {
    if (!canEdit) return;
    setIsEditing(true);
  };

  if (!employee) {
    return (
      <>
        <Header />
        <div className="d-flex" style={{ "--hiring-primary": primaryColor }}>
          <LeftNavBar />
          <div
            className="page-content-wrapper"
            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <div style={{ textAlign: "center", color: "#646569" }}>
              <p style={{ fontSize: 18, fontWeight: 500, marginBottom: 12 }}>
                Employee not found
              </p>
              <SecondaryButton onClick={handleBack}>Back to Employees</SecondaryButton>
            </div>
          </div>
        </div>
      </>
    );
  }

  const avatarBg = getAvatarColor(employee.fullName);
  const initials = getInitials(employee.fullName);
  const isActive = employee.employementStatus === "Active";
  const skills = employee.skills || [];
  const hasProfilePicture = !!employee.profilePictureUrl;

  const renderOverview = () => {
    const InfoCard = ({ title, Icon: TitleIcon, children }) => (
      <div className="ed-info-card">
        <p className="ed-info-card-title">
          {TitleIcon && <TitleIcon size={13} style={{ marginRight: 5 }} />}
          {title}
        </p>
        <div className="ed-info-card-grid">{children}</div>
      </div>
    );

    const Field = ({ label, value }) => (
      <div className="ed-info-field">
        <span className="ed-info-field-label">{label}</span>
        <span className="ed-info-field-value">{value || "—"}</span>
      </div>
    );

    return (
      <div className="ed-overview">
        <InfoCard title="Personal Information" Icon={MdPerson}>
          <Field label="First Name" value={employee.firstName} />
          <Field label="Last Name" value={employee.lastName} />
          <Field label="Email" value={employee.email} />
          <Field label="Personal Email" value={employee.personalEmail} />
          <Field label="Mobile" value={employee.mobileNumber} />
          <Field label="Date of Birth" value={employee.dateOfBirth} />
          <Field label="Gender" value={employee.gender} />
          <Field label="Blood Group" value={employee.bloodGroup} />
          <Field label="Address" value={employee.address} />
          <Field label="Emergency Contact Name" value={employee.emergencyContactName} />
          <Field label="Emergency Contact Number" value={employee.emergencyContactNumber} />
        </InfoCard>

        <InfoCard title="Employment Information" Icon={MdWorkOutline}>
          <Field label="Employee ID" value={employee.id} />
          <Field label="Role" value={employee.role} />
          <Field label="Designation" value={employee.designation} />
          <Field label="Department" value={employee.department} />
          <Field label="Lead" value={employee.leadName} />
          <Field label="Qualifications" value={employee.qualifications} />
          <Field label="Joining Date" value={employee.joiningDate} />
          <Field label="Total Experience" value={employee.totalExperience} />
          <Field label="Organization Experience" value={employee.organizationExperience} />
          <Field label="Employee Status" value={employee.employementStatus} />
          <Field label="Project Status" value={employee.employeeProjectStatus} />
        </InfoCard>

        <InfoCard title="Bank & Statutory Information" Icon={MdAccountBalance}>
          <Field label="Bank Name" value={employee.bankName} />
          <Field label="Bank Account Number" value={employee.bankAccountNumber} />
          <Field label="IFSC" value={employee.ifsc} />
          <Field label="PAN Number" value={employee.panNumber} />
          <Field label="PF Number" value={employee.pfNumber} />
          <Field label="UAN Number" value={employee.uanNumber} />
          <Field label="Health Card Number" value={employee.healthCardNumber} />
        </InfoCard>

        {skills.length > 0 && (
          <InfoCard title="Skills" Icon={MdStar}>
            <div className="ed-skill-chip-list" style={{ gridColumn: "1 / -1" }}>
              {skills.map((skill, i) => (
                <span key={i} className="ed-skill-chip">
                  {skill.skillName}
                  {skill.skillRating ? ` · ${parseInt(skill.skillRating) * 10}%` : ""}
                </span>
              ))}
            </div>
          </InfoCard>
        )}
      </div>
    );
  };

  const renderDocumentTab = ({ label, url }) => (
    <div className="ed-doc-tab">
      <div className="ed-doc-actions-bar">
        <p className="ed-doc-actions-label">{label}</p>
        {canEdit && (
          <SecondaryButton
            onClick={goToEditForUpload}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}
          >
            <MdUploadFile size={14} />
            {url ? "Change" : "Upload"}
          </SecondaryButton>
        )}
      </div>

      {url ? (
        <div className="ed-doc-viewer">
          <iframe src={url} title={label} className="ed-doc-iframe" />
        </div>
      ) : (
        <div className="ed-doc-empty">
          <div className="ed-doc-empty-icon">
            <MdBadge size={52} />
          </div>
          <p className="ed-doc-empty-title">No {label} Uploaded</p>
          <p className="ed-doc-empty-sub">Upload a PDF to attach the {label.toLowerCase()}.</p>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Header />
      <div className="d-flex" style={{ "--hiring-primary": primaryColor }}>
        <LeftNavBar />
        <div className="page-content-wrapper ed-detail-fade-in">

          <PageHeader>
            <PageHeaderName>
              <ArrowLeft
                size={20}
                onClick={handleBack}
                style={{ cursor: "pointer", marginRight: 8, flexShrink: 0 }}
              />
              Employee Details
            </PageHeaderName>

            {!isEditing && canEdit && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginRight: 20 }}>
                <PrimaryButton onClick={handleStartEdit}>Edit Employee</PrimaryButton>
              </div>
            )}
          </PageHeader>

          <div className="ed-summary-card">
            <div className="ed-profile-row">
              {hasProfilePicture ? (
                <img
                  src={employee.profilePictureUrl}
                  alt={employee.fullName}
                  className="ed-avatar-img"
                />
              ) : (
                <div className="ed-avatar" style={{ background: avatarBg }}>{initials}</div>
              )}
              <div className="ed-profile-info">
                <h1 className="ed-employee-name">{employee.fullName}</h1>
                <p className="ed-designation-sub">{employee.designation}</p>
                <p className="ed-id-meta">
                  {employee.id}
                  {employee.department && ` · ${employee.department}`}
                </p>
              </div>
            </div>

            <div className="ed-stat-strip">
              <div className="ed-stat-item">
                <span
                  className="ed-stat-status-chip"
                  style={
                    isActive
                      ? { background: "#e8f5e9", color: "#2e7d32" }
                      : { background: "#ffebee", color: "#c62828" }
                  }
                >
                  {employee.employementStatus}
                </span>
                <span className="ed-stat-label">Status</span>
              </div>
              <div className="ed-stat-item">
                <span className="ed-stat-value">{employee.totalExperience || "—"}</span>
                <span className="ed-stat-label">Total Experience</span>
              </div>
              <div className="ed-stat-item">
                <span className="ed-stat-value">{employee.role || "—"}</span>
                <span className="ed-stat-label">Role</span>
              </div>
              <div className="ed-stat-item">
                <span className="ed-stat-value">{employee.leadName || "—"}</span>
                <span className="ed-stat-label">Lead</span>
              </div>
            </div>
          </div>

          <div className="ed-content-box">
            {!isEditing && visibleTabs.length > 0 && (
              <div className="ed-tab-bar">
                {visibleTabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={activeTab === tab.id ? "ed-tab ed-tab--active" : "ed-tab"}
                    style={
                      activeTab === tab.id
                        ? { color: primaryColor, borderBottomColor: primaryColor }
                        : {}
                    }
                    onClick={() => goToTab(tab)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            <div key={isEditing ? "edit" : activeTab} className="ed-tab-content ed-tab-content-fade">
              {isEditing && canEdit ? (
                <div className="ed-edit-wrap">
                  <EditEmployeeModal
                    singleEmployeeData={employee}
                    leads={leads}
                    closeEditEmployeeModel={closeEditEmployeeModel}
                    notifyUpdated={notifyUpdated}
                    fetchEmployees={refreshEmployee}
                  />
                </div>
              ) : (
                <>
                  {activeTab === "overview" && hasPermission(FEATURE_CODES.EMPLOYEE_OVERVIEW_VIEW) && renderOverview()}
                  {activeTab === "aadhar" && hasPermission(FEATURE_CODES.EMPLOYEE_AADHAR_VIEW) && renderDocumentTab({
                    label: "Aadhar Card",
                    url: employee.aadharCardUrl,
                  })}
                  {activeTab === "pan" && hasPermission(FEATURE_CODES.EMPLOYEE_PAN_VIEW) && renderDocumentTab({
                    label: "PAN Card",
                    url: employee.panCardUrl,
                  })}
                  {visibleTabs.length === 0 && (
                    <div style={{ textAlign: "center", color: "#646569", padding: "40px 0" }}>
                      You don't have permission to view any employee details tab.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default EmployeeDetailsPage;
